import { SpendLevelType } from '@aptitude/shared-vanilla';

import { DateRange } from '../../modules/date-range/date-range';
import { Identifier } from '../identifiers/identifier';
import {
  CategoryIdentifier,
  BuyerIdentifier,
  SellerIdentifier,
} from '../types';
import moment from 'moment-timezone';

export type ContractStatus =
  | 'BuyerExecuted'
  | 'FullyExecuted'
  | 'Active'
  | 'Expired'
  | 'Terminated'
  | 'Canceled';
export type CommitmentType =
  | 'INDIVIDUAL_COMMITMENT'
  | 'DUAL_SOURCE'
  | 'GROUP_PLAN'
  | 'IDN';
export type StatusLabelType =
  | 'SIGNATURE_NEEDED'
  | 'NEW'
  | 'EXPIRING_SOON'
  | 'RECENTLY_EXPIRED'
  | 'LONG_EXPIRED'
  | null;

export enum TIMEFRAME {
  'OFFICIAL' = 'OFFICIAL',
  'GAP' = 'GAP',
  'RAMP_UP' = 'RAMP_UP',
  'TIMEFRAME' = 'TIMEFRAME',
}

export interface EmbeddedContract {
  id: number;
  publishedId: string;
  effectiveDate: number;
  seller: {
    id: number;
    name: string;
    hasSigned: boolean;
    signedBy: string;
    daysToSign: number;
    contractSignedOn: number;
    contractSigningUrl: string;
    canSignContract: boolean;
    offerId: number;
    offerName: string;
    hasValidCpaTemplate: boolean;
    leadTimeDays: number;
  };
  buyer: {
    id: number;
    name: string;
    hasSigned: boolean;
    signedBy: {
      id: number;
      name: string;
    };
    daysToSign: number;
    contractSignedOn: number;
    contractSigningUrl: string;
    canSignContract: boolean;
    organizationType: unknown;
    purchasePlanStatus: {
      planId: number;
      numberOfPartnersAccepted: number;
      numberOfPartnersInPlan: number;
      planType: string;
      isMasterPlan: boolean;
      acceptedPlan: boolean;
    };
    masterPlannedMarketShare: number;
    plannedMarketShare: number;
    purchasePlan: {
      planId: number;
      numberOfPartnersAccepted: number;
      numberOfPartnersInPlan: number;
      planType: string;
      isMasterPlan: boolean;
      acceptedPlan: boolean;
    };
  };
  duration: number;
  pricingType: string;
  marketShare: number;
  supportedOfferTerm: {
    pricingTypes: Array<{
      name: string;
      value: string;
      supports: boolean;
    }>;
  };
  status: string;
  numberOfRebates: number;
  possibleEffectiveDates: Array<number>;
  memberPlannedSpendLevel: number;
  targetSpendLevel: {
    type: {
      name: string;
      displayName: string;
      fieldNames: Array<string>;
    };
    dollarMarketShare: number;
    unitMarketShare: number;
    absoluteDollars: number;
    absoluteUnits: number;
  };
  documentLocation: string;
}

export interface RfpWithEmbeddedContracts {
  rfpId: number;
  rfpType: string;
  category: { id: number; name: string };
  rfpStatus: string;
  isPricingExpired: boolean;
  buyer: {
    id: number;
    name: string;
    aggreementStatus: string;
    categorySpend: {
      last12monthSpend: number;
      acttualSpend: number;
      actualSpend: number;
      continuedSpend: number;
      categoryPotentialSpend: number;
      spendWithSeller: number;
      next12monthSpend: number;
      crossReferenceSpend: number;
      categorySavings: number;
    };
    isPartner: boolean;
    memberId: string;
  };
  contracts: Array<EmbeddedContract>;
}

export interface ContractGraphqlResponse {
  id: number;
  publishedId: string;
  isGroup: boolean;
  status: ContractStatus;
  statusLabel: StatusLabelType;
  effectiveDate: string;
  expirationDate: string;
  agreementType: string;
  commitmentType: string;
  spendLevelType: SpendLevelType;
  characteristicSummary: CharacteristicSummary;
  category: CategoryIdentifier;
  buyer: BuyerIdentifier;
  seller: SellerIdentifier;
  userOrgAverage: ContractUserOrgAverage;
  daysUntilMeasurable: number;
  currentTarget: ContractComplianceValue[];
  parent: Partial<ContractGraphqlResponse> | null;
  contractRfpAudit: ContractRfpAudit | null;
  categorizationScheme: CategorizationSchemes;
}

export interface CharacteristicSummary {
  reveal: boolean;
  compliance: boolean;
}
export interface ContractCreationRequest {
  agreementType: string;
  buyerId: number;
  categoryId: number;
  sellerId: number;
  effectiveDate: string;
  expirationDate: string;
  commitmentType: string;
  tierType: string;
  publishedId: string;
}

export interface UpdateContractTierRequest {
  contractId: number;
  tierId: number;
  name: number;
  tierType: string;
  description: string;
}

export interface PartnerCounts {
  greenPartnerCount: number | null;
  yellowPartnerCount: number | null;
  redPartnerCount: number | null;
  whitePartnerCount: number | null;
}

export interface ContractCommitmentValues {
  value: number | null;
  target: number | null;
  valueType: string;
  formatType: string;
}

export class Contract {
  parent?: Contract;
  buyer!: Identifier;
  category!: Identifier;
  subType: ContractSubType;
  commitmentType!: CommitmentType | null;
  effectiveDate!: string;
  expirationDate!: string;
  id!: number;
  isGroup!: boolean;
  isAccessible!: boolean | null;
  publishedId!: string;
  // MEAS_1301_MULTISOURCE deprication delete this and replace with contractSellers
  seller!: Identifier;
  contractedSellers!: Identifier[];
  status!: ContractStatus;
  terminationDate!: string;
  sellerExecutionDate?: string;
  buyerExecutionDate?: string;
  characteristics!: Array<{ key: string; value: string }>;
  spendLevelType!: SpendLevelType;
  contractAverage?: ComplianceForTimeRange;
  contractSnapshot?: ComplianceForTimeRange;
  userOrgAverage?: ComplianceForTimeRange;
  userOrgSnapshot?: ComplianceForTimeRange;
  initialTarget?: ComplianceValue[];
  currentTarget?: ComplianceValue[];
  tierAdjustment?: TierAdjustment;
  statusLabel?: StatusLabelType;
  alert?: { daysLeft: number };
  rfpId?: number;
  activeTierHistory!: ActiveTierHistory[];
  contractRfpAudit?: ContractRfpAudit;
  categorizationScheme!: CategorizationSchemes;
  tierParticipationType!: ParticipationType;

  constructor(contractGQL: ContractGQL) {
    Object.assign(this, contractGQL);
    if (contractGQL.contractAverage) {
      this.contractAverage =
        mapComplianceForTimeRangeGQLToComplianceForTimeRange(
          contractGQL.contractAverage,
        );
    }
    if (contractGQL.contractSnapshot) {
      this.contractSnapshot =
        mapComplianceForTimeRangeGQLToComplianceForTimeRange(
          contractGQL.contractSnapshot,
        );
    }
    if (contractGQL.userOrgAverage) {
      this.userOrgAverage =
        mapComplianceForTimeRangeGQLToComplianceForTimeRange(
          contractGQL.userOrgAverage,
        );
    }
    if (contractGQL.userOrgSnapshot) {
      this.userOrgSnapshot =
        mapComplianceForTimeRangeGQLToComplianceForTimeRange(
          contractGQL.userOrgSnapshot,
        );
    }
    if (contractGQL.initialTarget) {
      this.initialTarget = contractGQL.initialTarget.map(
        mapComplianceValueGQLToComplianceValue,
      );
    }
    if (contractGQL.currentTarget) {
      this.currentTarget = contractGQL.currentTarget.map(
        mapComplianceValueGQLToComplianceValue,
      );
    }

    // MEAS_1301_MULTISOURCE deprication delete this
    if (
      contractGQL.contractedSellers &&
      contractGQL.contractedSellers.length > 0
    ) {
      this.seller = contractGQL.contractedSellers[0];
    }

    if (contractGQL.parent) {
      this.parent = new Contract(contractGQL.parent);
    }

    this.tierParticipationType = getParticipationType(
      this.singleTierParticipation,
      this.multisourceTierParticipation,
    );
  }

  get hasReveal(): boolean {
    return (
      this.characteristics.find((item) => item.key === 'REVEAL_MEASUREMENT')
        ?.value === 'Y'
    );
  }

  get hasCompliance(): boolean {
    return (
      this.characteristics.find((item) => item.key === 'CPA_COMPLIANCE')
        ?.value === 'Y'
    );
  }

  get isOldMultiSource(): boolean {
    return Boolean(
      this.parent === null &&
        this.characteristics.find((item) => item.key === 'Multi-Source')
          ?.value === 'Y',
    );
  }

  get isAggregateMultiSource(): boolean {
    return Boolean(this.parent?.id);
  }

  get isIndividualCommit(): boolean {
    return this.commitmentType === 'INDIVIDUAL_COMMITMENT';
  }

  get isAggregate(): boolean {
    return this.subType === 'AGGREGATE';
  }

  get pricingType(): PricingType {
    return this.characteristics.find((item) => item.key === 'Pricing Type')
      ?.value as PricingType;
  }

  get isPsc(): boolean {
    return this.categorizationScheme === 'PSC';
  }

  get mixedTierParticipation(): boolean {
    return this.multisourceTierParticipation && this.singleTierParticipation;
  }

  // if leader review Contract Average / Snapshot
  // if partner o Seller userOrgAverage UserOrgSnapshot
  get multisourceTierParticipation(): boolean {
    return (
      validateParticipation(this.parent?.contractAverage) ||
      validateParticipation(this.parent?.contractSnapshot)
    );
  }

  get singleTierParticipation(): boolean {
    return (
      validateParticipation(this.contractAverage) ||
      validateParticipation(this.contractSnapshot)
    );
  }
}

function getParticipationType(
  singleTierParticipation: boolean,
  multisourceTierParticipation: boolean,
): ParticipationType {
  if (singleTierParticipation && !multisourceTierParticipation) {
    return 'SINGLE';
  }

  if (!singleTierParticipation && multisourceTierParticipation) {
    return 'MULTI';
  }

  return 'MIXED';
}

function mapComplianceForTimeRangeGQLToComplianceForTimeRange({
  assessment,
  comboType,
  dateRange,
  periodDateRange,
  targets,
  values,
  complianceForPartnerCount,
  complianceByMonth,
  periodMessage = '',
  timeframe,
  tierSource,
}: ComplianceForTimeRangeGQL): ComplianceForTimeRange {
  return {
    assessment,
    comboType,
    dateRange: {
      fromDate: moment(dateRange?.fromDate).toDate(),
      toDate: moment(dateRange?.toDate).toDate(),
    },
    periodDateRange: {
      fromDate: periodDateRange?.fromDate as unknown as Date,
      toDate: periodDateRange?.toDate as unknown as Date,
    },
    targets: (targets || []).map(mapComplianceValueGQLToComplianceValue),
    values: (values || []).map(mapComplianceValueGQLToComplianceValue),
    complianceForPartnerCount,
    complianceByMonth,
    periodMessage,
    timeframe,
    tierSource,
  };
}

function mapComplianceValueGQLToComplianceValue({
  value,
  valueType,
}: ComplianceValueGQL): ComplianceValue {
  return {
    value,
    valueType: complianceTypeMapper[valueType],
  };
}

function validateParticipation(
  struct: ComplianceForTimeRange | undefined,
): boolean {
  return Boolean(
    struct?.complianceForPartnerCount &&
      struct.complianceForPartnerCount.length > 0,
  );
}

export type ParticipationType = 'SINGLE' | 'MULTI' | 'MIXED';

export type Assessment =
  | 'COMPLIANT'
  | 'ALMOST_COMPLIANT'
  | 'UNDER_COMPLIANT'
  | 'NO_MEASUREMENT';
export type ComboType = 'AND' | 'OR' | 'NA';

export interface ComplianceForTimeRange {
  assessment: Assessment;
  comboType: ComboType;
  values: ComplianceValue[];
  targets: ComplianceValue[];
  dateRange: DateRange;
  periodDateRange: DateRange;
  greenPartnerCount?: number;
  yellowPartnerCount?: number;
  redPartnerCount?: number;
  complianceForPartnerCount?: ComplianceForPartnerCount[];
  complianceByMonth?: ComplianceByMonth[];
  periodMessage: string;
  timeframe?: TIMEFRAME;
  tierSource?: ParticipationType;
}

export interface ComplianceByMonth {
  yearMonth: string;
  targets: ComplianceValue[];
  values: ComplianceValue[];
}

export interface ComplianceForPartnerCount {
  yearMonth: string;
  greenPartnerCount: number;
  redPartnerCount: number;
  yellowPartnerCount: number;
  whitePartnerCount: number;
}

export interface ComplianceValue {
  value: number;
  valueType: ComplianceTypeValue;
}

export interface ContractUserOrgAverage {
  comboType: 'AND' | 'OR' | 'NA' | null;
  assessment: Assessment;
  values: ContractComplianceValue[];
  targets: ContractComplianceValue[];
  complianceForPartnerCount: PartnerCounts[];
}

export interface ContractComplianceValue {
  value: number | null;
  valueType: string;
}

export enum ComplianceTypeEnum {
  DOLLAR_MARKET_SHARE = 'DOLLAR_MARKET_SHARE',
  UNIT_MARKET_SHARE = 'UNIT_MARKET_SHARE',
  DOLLAR_VOLUME = 'DOLLAR_VOLUME',
  UNIT_VOLUME = 'UNIT_VOLUME',
  PARTICIPANTS = 'PARTICIPANTS',
}

export type DisplayType = 'dollars' | 'number' | 'percent';

export interface ComplianceTypeValue {
  displayType: DisplayType;
  displayName: string;
  incrementalTargetApplicable?: boolean;
  shouldInvertValueFromTarget?: boolean;
}

const complianceTypeMapper: ComplianceTypeMapper = {
  [ComplianceTypeEnum.DOLLAR_MARKET_SHARE]: {
    displayName: 'Spend Market Share',
    displayType: 'percent',
  },
  [ComplianceTypeEnum.UNIT_MARKET_SHARE]: {
    displayName: 'Unit Market Share',
    displayType: 'percent',
  },
  [ComplianceTypeEnum.DOLLAR_VOLUME]: {
    displayName: 'Dollar Volume',
    displayType: 'dollars',
    incrementalTargetApplicable: true,
  },
  [ComplianceTypeEnum.UNIT_VOLUME]: {
    displayName: 'Unit Volume',
    displayType: 'number',
    incrementalTargetApplicable: true,
  },
  [ComplianceTypeEnum.PARTICIPANTS]: {
    displayName: 'Individual Commit',
    displayType: 'number',
    shouldInvertValueFromTarget: true,
  },
};

type ComplianceTypeMapper = {
  [key in ComplianceTypeEnum]: ComplianceTypeValue;
};

export interface ContractGQL {
  __typename: 'Contract';
  parent?: ContractGQL;
  buyer: Identifier;
  category: Identifier;
  subType?: ContractSubType;
  commitmentType: CommitmentType | null;
  effectiveDate: string;
  expirationDate: string;
  id: number;
  isGroup: boolean;
  publishedId: string;
  seller: Identifier;
  isAccessible?: boolean | null;
  contractedSellers: Identifier[];
  status: ContractStatus;
  terminationDate: string;
  sellerExecutionDate?: string;
  buyerExecutionDate?: string;
  characteristics: Array<{ key: string; value: string | undefined }>;
  spendLevelType: SpendLevelType;
  contractAverage?: ComplianceForTimeRangeGQL;
  contractSnapshot?: ComplianceForTimeRangeGQL;
  userOrgAverage?: ComplianceForTimeRangeGQL;
  userOrgSnapshot?: ComplianceForTimeRangeGQL;
  initialTarget?: ComplianceValueGQL[];
  currentTarget?: ComplianceValueGQL[];
  tierAdjustment: TierAdjustment;
  activeTierHistory: ActiveTierHistory[];
  categorizationScheme: CategorizationSchemes;
}

export type CategorizationSchemes = 'APTITUDE' | 'PSC' | 'UNSPSC' | null;

export interface ComplianceForTimeRangeGQL {
  assessment: Assessment;
  comboType: ComboType;
  values: ComplianceValueGQL[];
  targets: ComplianceValueGQL[];
  dateRange: DateRangeGQL;
  periodDateRange: DateRangeGQL;
  complianceForPartnerCount: ComplianceForPartnerCountGQL[];
  complianceByMonth: ComplianceByMonth[];
  periodMessage: string;
  timeframe: TIMEFRAME;
  tierSource?: ParticipationType;
}

export interface DateRangeGQL {
  fromDate: string;
  toDate: string;
}

export interface ComplianceValueGQL {
  value: number;
  valueType: ComplianceTypeEnum;
}

export interface ComplianceForPartnerCountGQL {
  yearMonth: string;
  greenPartnerCount: number;
  redPartnerCount: number;
  yellowPartnerCount: number;
  whitePartnerCount: number;
}

export type PricingType = 'Fixed' | 'Variable' | undefined;
export type ContractSubType = 'AGGREGATE' | 'UMBRELLA' | undefined;

interface TierAdjustment {
  adjustmentDueDate: string;
  notificationDueDate: string;
  canRequest: boolean;
  requested: 'NOT' | 'BUYER' | 'SELLER';
}

export enum ContractView {
  CONTRACT,
  MULTISOURCE,
}
export interface ActiveTierHistory {
  id: number;
  startDate: string;
  endDate: string;
}

export interface ContractRfpAudit {
  id: number;
  rfpId: number;
  status: 'NEEDS_CREATION' | 'IN_PROGRESS' | 'DONE';
}

export interface MultiSourceChildContract {
  id: number;
  publishedId: string;
  seller: Identifier;
  buyer: Identifier;
  category: Identifier;
  isAccessible: boolean | null;
}
